import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 [&_svg]:size-4 [&_svg]:shrink-0",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 [&_svg]:size-4 [&_svg]:shrink-0",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 [&_svg]:size-4 [&_svg]:shrink-0",
        ghost:
          "hover:bg-accent hover:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0",
        link: "relative group cursor-pointer",
      },
      size: {
        default: "text-sm px-2 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
      // Link-specific padding sizes for the background
      linkSize: {
        default: "", // Will use default padding defined in component
        sm: "", // Will use small padding defined in component
        md: "", // Will use medium padding defined in component
        lg: "", // Will use large padding defined in component
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      linkSize: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const NavButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, linkSize, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    // Add a wrapper for link variant to create the hover effect
    if (variant === "link") {
      // Apply a direct hover background on the button to avoid persistent backgrounds
      // and compute padding based on linkSize to increase the hit area.
      let paddingClass = "p-2"; // default large
      if (linkSize === "sm") paddingClass = "p-1";
      if (linkSize === "md") paddingClass = "p-1.5";
      if (linkSize === "lg") paddingClass = "p-2";

      const baseClasses = cn(
        buttonVariants({ variant, size, linkSize }),
        "hover:bg-accent rounded-sm",
        paddingClass,
        className
      );

      // When asChild is used, ensure the child element takes full size
      if (asChild) {
        return (
          <Comp className={baseClasses} ref={ref} {...props}>
            {props.children}
          </Comp>
        );
      }

      return (
        <Comp className={baseClasses} ref={ref} {...props}>
          <span className="inline-flex items-center">{props.children}</span>
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, linkSize, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
NavButton.displayName = "Button";

export { NavButton, buttonVariants };
